'use client'

import React, {useEffect, useState} from 'react'
import {tv} from 'tailwind-variants'
import {grid, gridItem} from '~/design-system/foundations'
import {Table} from '~/design-system/foundations/Table'
import {useIsLargerThanMobile} from '~/design-system/hooks/useMediaQuery'
import {type HgPricingTableProps} from './types'
import {kebabCase} from 'lodash'
import {
  HgTableCollapsibleSection,
  HgTableTitleComparisonRow,
} from '~/design-system/hg/components/HgTable'
import {HgPricingTableRow} from './_components/PricingTableRow'
import {type ComparisonSelection} from '~/design-system/hg/components/HgTable/types'
import {useHash} from '~/hooks/useHash'

const hgTableVariants = tv({
  base: 'relative border-border-default text-text-default',
  slots: {
    tableWrapper: 'overflow-visible pb-s9',
    wrapper: 'mx-auto w-full',
    gridItem: 'max',
  },
})

export default function HgPricingTable({sections, planTitles}: HgPricingTableProps) {
  const {tableWrapper, wrapper} = hgTableVariants()
  const isNotMobile = useIsLargerThanMobile()
  const hash = useHash()

  const planOptions = planTitles.map(label => {
    const comparisonName = `${label.defaultText}${label.primaryText ? ` ${label.primaryText}` : ''}`
    return {
      value: kebabCase(comparisonName),
      label: comparisonName,
    }
  })

  const createAllSectionsOpenState = (isOpen: boolean) =>
    Object.fromEntries(sections.map(section => [section.id, isOpen]))

  const [selectedPlans, setSelectedPlans] = useState<ComparisonSelection>({
    primaryComparison: planOptions[0].value,
    secondaryComparison: planOptions[1]?.value || '',
  })
  const [openSections, setOpenSections] = useState(createAllSectionsOpenState(false))
  const [areAllSectionsOpen, setAreAllSectionsOpen] = useState(false)

  useEffect(() => {
    setAreAllSectionsOpen(Object.values(openSections).every(isOpen => isOpen))
  }, [openSections])

  const toggleSection = (sectionId: string) => {
    setOpenSections({...openSections, [sectionId]: !openSections[sectionId]})
  }

  const toggleAllSections = () => {
    setOpenSections(createAllSectionsOpenState(!areAllSectionsOpen))
  }

  const handleSelectionChange = (selection: ComparisonSelection) => {
    setSelectedPlans(selection)
  }

  useEffect(() => {
    const section = sections.find(s => s.id === hash.slice(1))

    if (!section) return

    setOpenSections(prev => ({
      ...prev,
      [section.id]: true,
    }))
  }, [hash, sections])

  return (
    <div className="w-full">
      <div className={grid({className: 'mx-auto'})}>
        <div
          className={gridItem({
            size: 'max',
            className: wrapper(),
          })}
        >
          <Table
            classNames={{
              wrapper: tableWrapper(),
            }}
          >
            <HgTableTitleComparisonRow
              comparatorLabels={planTitles}
              selectedComparisons={selectedPlans}
              onSelectionChange={handleSelectionChange}
              comparisonOptions={planOptions}
              hasCollapsibleSections
              onToggleAllSections={toggleAllSections}
              areAllSectionsOpen={areAllSectionsOpen}
            />
            {sections.map(section => (
              <HgTableCollapsibleSection
                key={section.id}
                id={section.id}
                icon={section.sectionTitle.leftIconType}
                heading={section.sectionTitle.title}
                onOpenChange={() => {
                  toggleSection(section.id)
                }}
                open={openSections[section.id]}
              >
                <>
                  {section.rows.map((row, rowIndex) => (
                    <HgPricingTableRow
                      key={rowIndex}
                      row={row}
                      selectedPlans={selectedPlans}
                      isNotMobile={isNotMobile}
                      planOptions={planOptions}
                    />
                  ))}
                </>
              </HgTableCollapsibleSection>
            ))}
          </Table>
        </div>
      </div>
    </div>
  )
}
